import { useEffect } from "react";
import { Modal } from "antd";
import { CloseOutlined, CreditCardOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { modalStyles } from "../../../../utils/modalStyles";
import type {
  Device,
  DevicePaymentMethod,
} from "../../../../api/devices";
import type { PaymentMethod } from "../../../../api/paymentMethods";

const R = {
  surface: "var(--color-surface)",
  bg: "var(--color-canvas)",
  border: "var(--color-shade-2)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
  brandDeep: "var(--color-brand-deep-ink)",
  brandTint: "var(--color-brand-tint)",
  mono: '"JetBrains Mono", ui-monospace, monospace',
};

interface Props {
  open: boolean;
  device: Device | null;
  paymentMethods: PaymentMethod[];
  onClose: () => void;
}

const DevicePaymentMethodsViewModal = ({
  open,
  device,
  paymentMethods,
  onClose,
}: Props) => {
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();
  const lang = i18n.language?.startsWith("en") ? "en" : "bg";

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // preventDefault: без него същото натискане минава и през глобалния
        // Esc-„назад" на SharedAccountGate (правило от CLAUDE.md)
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const modalWidth = isMobile ? undefined : isTablet ? 420 : 460;
  const items: DevicePaymentMethod[] = device?.paymentMethods ?? [];
  const sorted = [...items].sort((a, b) =>
    a.value.localeCompare(b.value, undefined, { numeric: true }),
  );
  const primaryName = (pm: PaymentMethod) =>
    lang === "en" ? pm.name_en : pm.name_bg;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={modalWidth}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        style={{
          background: R.surface,
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: isNarrow ? "14px 16px" : "20px 24px",
            borderBottom: `1px solid ${R.border}`,
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: R.brandTint,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: R.brandDeep,
              flexShrink: 0,
            }}
          >
            <CreditCardOutlined style={{ fontSize: 18 }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: isNarrow ? 16 : 17,
                fontWeight: 700,
                letterSpacing: -0.3,
                color: R.ink,
                lineHeight: 1.25,
              }}
            >
              {t("devicePaymentMethods.viewTitle")}
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: R.textSoft,
                lineHeight: 1.4,
                marginTop: 4,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {device?.name ?? ""}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              height: 30,
              width: 30,
              borderRadius: 6,
              background: "transparent",
              border: "none",
              color: R.textMuted,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 120ms, color 120ms",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--color-shade-1)";
              e.currentTarget.style.color = R.ink;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = R.textMuted;
            }}
          >
            <CloseOutlined style={{ fontSize: 14 }} />
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: isNarrow ? "14px 16px" : "18px 22px",
            background: R.bg,
          }}
        >
          {sorted.length === 0 ? (
            <div
              style={{
                padding: 28,
                textAlign: "center",
                color: R.textMuted,
                fontSize: 13,
                background: R.surface,
                border: `1px dashed ${R.border}`,
                borderRadius: 12,
              }}
            >
              {t("devicePaymentMethods.viewEmpty")}
            </div>
          ) : (
            <div
              style={{
                background: R.surface,
                border: `1px solid ${R.border}`,
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              {/* Header row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "10px 14px",
                  background: R.bg,
                  borderBottom: `1px solid ${R.border}`,
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: R.textMuted,
                  letterSpacing: 0.8,
                  textTransform: "uppercase",
                }}
              >
                <span style={{ flex: 1 }}>
                  {t("devicePaymentMethods.colMethod")}
                </span>
                <span style={{ width: 80, textAlign: "right" }}>
                  {t("devicePaymentMethods.colSlot")}
                </span>
              </div>
              {sorted.map((dp, idx) => {
                const pm = paymentMethods.find(
                  (p) => p.id === dp.paymentMethodId,
                );
                const label = pm
                  ? primaryName(pm)
                  : `#${dp.paymentMethodId}`;
                const isLast = idx === sorted.length - 1;
                return (
                  <div
                    key={dp.paymentMethodId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "12px 14px",
                      borderBottom: isLast
                        ? "none"
                        : `1px solid ${R.border}`,
                    }}
                  >
                    <span
                      style={{
                        flex: 1,
                        fontSize: 13,
                        fontWeight: 600,
                        color: R.ink,
                        letterSpacing: -0.1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {label}
                    </span>
                    <span
                      style={{
                        width: 80,
                        textAlign: "right",
                        fontFamily: R.mono,
                        fontSize: 13,
                        fontWeight: 700,
                        color: R.brandDeep,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {dp.value}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: `1px solid ${R.border}`,
            padding: isNarrow ? "12px 16px" : "14px 22px",
            display: "flex",
            justifyContent: "flex-end",
            background: R.surface,
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              height: 42,
              padding: "0 22px",
              borderRadius: 10,
              border: `1px solid ${R.border}`,
              background: "transparent",
              color: R.textSoft,
              fontSize: 13.5,
              fontWeight: 600,
              cursor: "pointer",
              transition: "background 120ms",
              width: isMobile ? "100%" : undefined,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--color-shade-1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            {t("common.close")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DevicePaymentMethodsViewModal;
